import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Page, PageTitleWithButton } from '../../../../components';
import { Theme, Box, Button, AppBar, Tab, Tabs } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Add } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { OptionSets } from './options-sets';
import { ColorPaletteSets } from './color-palette-sets/color-palette-sets';
import { useAllowedNavRoutes, useFlags } from '../../../../hooks';
import { filterNavLinksForAccess } from '../../../../helpers';
import { UserContext } from '../../../../context';

interface ISetsAndPalettes {}

export enum OptionSetType {
  Option = 'Option',
  StandardFeature = 'StandardFeature',
  ColorPalette = 'ColorPalette'
}

export const getSetType = (OptionSet: string) => {
  switch (OptionSet) {
    case OptionSetType.Option:
      return 'Option';
    case OptionSetType.StandardFeature:
      return 'Standard Feature';
    case OptionSetType.ColorPalette:
      return 'Color Palette';

    default:
      return 'Set';
  }
};

export const SetsAndPalettes: FC<ISetsAndPalettes> = () => {
  const classes = useStyles();
  const userContext = useContext(UserContext);
  const featureFlags = useFlags();
  const [selectedTab, setSelectedTab] = useState(window.location.pathname);
  const [selectedPage, setSelectedPage] = useState<OptionSetType>(OptionSetType.Option);
  const [selectedSetOption, setSelectedSetOption] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const history = useHistory();

  const { availableLinks } = useAllowedNavRoutes(featureFlags);

  useEffect(() => {
    if (window.location.href.includes('option-sets')) {
      setSelectedPage(OptionSetType.Option);
    }
    if (window.location.href.includes('standard-feature-sets')) {
      setSelectedPage(OptionSetType.StandardFeature);
    }
    if (window.location.href.includes('color-palette-sets')) {
      setSelectedPage(OptionSetType.ColorPalette);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href]);
  const optionSetsLists = useMemo(
    () => {
      const links = [
        {
          name: 'Option Sets',
          url: '/development/options/option-sets-and-palettes/option-sets',
          friendlyUrl: 'Development/Options/Option Sets And Palettes/Option Sets'
        },
        {
          name: 'Standard Feature Sets',
          url: '/development/options/option-sets-and-palettes/standard-feature-sets',
          friendlyUrl: 'Development/Options/Option Sets And Palettes/Standard Feature Sets'
        },
        {
          name: 'Color Palette Sets',
          url: '/development/options/option-sets-and-palettes/color-palette-sets',
          friendlyUrl: 'Development/Options/Option Sets And Palettes/Color Palette Sets'
        }
      ];
      if (featureFlags?.authorizeUserNavigationAccess) {
        return filterNavLinksForAccess(
          links,
          userContext,
          featureFlags,
          availableLinks.add('Development/Options/Option Sets And Palettes/Option Sets')
        );
      }
      return links;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [featureFlags]
  );

  return (
    <Page title="Option Sets and Palettes">
      <PageTitleWithButton
        pageTitleProps={{
          title: 'Option Sets and Palettes'
        }}
        button={
          <Box display="flex">
            <Button
              id="add-option"
              startIcon={<Add />}
              color="primary"
              variant="contained"
              className={classes.addOptionButton}
              onClick={() => {
                setSelectedSetOption(null);
                setIsModalOpen(true);
              }}
            >
              Add {getSetType(selectedPage)}
            </Button>
          </Box>
        }
      />
      {optionSetsLists && optionSetsLists.length > 0 && (
        <AppBar position="static" color="default" component="div" className={classes.tabBar}>
          <Tabs
            value={selectedTab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(_, value) => {
              setSelectedTab(value);
              history.push(value);
            }}
            variant="scrollable"
          >
            {optionSetsLists.map(link => {
              return (
                <Tab
                  value={link.url}
                  key={link.name}
                  id={`${link.name}-information`}
                  aria-controls={`${link.name}-information`}
                  label={link.name}
                />
              );
            })}
          </Tabs>
        </AppBar>
      )}
      {selectedPage === OptionSetType.Option && (
        <OptionSets
          selectedSetOption={selectedSetOption}
          setSelectedSetOption={setSelectedSetOption}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          OptionSetType={OptionSetType.Option}
        />
      )}
      {selectedPage === OptionSetType.StandardFeature && (
        <OptionSets
          selectedSetOption={selectedSetOption}
          setSelectedSetOption={setSelectedSetOption}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          OptionSetType={OptionSetType.StandardFeature}
        />
      )}
      {selectedPage === OptionSetType.ColorPalette && (
        <ColorPaletteSets
          selectedSetOption={selectedSetOption}
          setSelectedSetOption={setSelectedSetOption}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          OptionSetType={OptionSetType.ColorPalette}
        />
      )}
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  link: {
    textDecoration: 'none'
  },
  addOptionButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  actionButtons: {
    textAlign: 'right',
    display: 'flex'
  },
  tabBar: {
    marginTop: '-1rem',
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  buttonText: {
    color: 'white'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
